import React, { useState, useEffect } from 'react';

const ReferralIcon = ({ onOpenModal }) => {
  const [phase, setPhase] = useState(1); // 1: subtle, 2: pulsing, 3: hover
  const [timeElapsed, setTimeElapsed] = useState(false);
  const [scrollThreshold, setScrollThreshold] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Check if current page is qualifying
  const isQualifyingPage = () => {
    const path = window.location.pathname;
    const qualifyingPaths = [
      '/',
      '/services',
      '/resources/mental-health',
      '/testimonials',
      '/team',
      '/services/comprehensive-mental-health',
      '/services/outpatient-mental-health',
      '/services/medication-management',
      '/services/treatment-plans',
      '/services/psychiatric-rehabilitation',
      '/services/community-resources',
      '/services/comprehensive-assessment'
    ];
    return qualifyingPaths.some(qPath => path === qPath || path.startsWith('/services'));
  };

  useEffect(() => {
    // Only activate triggers on qualifying pages
    if (!isQualifyingPage()) {
      return;
    }

    // Time-based trigger: 30 seconds
    const timeTimer = setTimeout(() => {
      setTimeElapsed(true);
    }, 30000);

    // Scroll-based trigger: 75% of page
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrollPercentage = (scrollTop / scrollHeight) * 100;

      if (scrollPercentage >= 75) {
        setScrollThreshold(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Activate Phase 2 (pulsing) when both conditions are met
  useEffect(() => {
    if (timeElapsed && scrollThreshold && phase === 1) {
      setPhase(2);
    }
  }, [timeElapsed, scrollThreshold, phase]);

  // Handle hover (Phase 3) - now works at any phase
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Keyboard focus mirrors hover state so the tooltip is reachable via Tab
  const handleFocus = () => {
    setIsHovered(true);
  };

  const handleBlur = () => {
    setIsHovered(false);
  };

  // Handle click (Phase 4) - now works at any phase
  const handleClick = () => {
    onOpenModal();
  };

  // Determine icon color based on phase
  const getIconColor = () => {
    if (isHovered) return '#499F94'; // Phase 3: Moss green
    if (phase >= 2) return '#66C2B6'; // Phase 2: Soft teal
    return '#A9D1C9'; // Phase 1: Light grey-teal
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="flex items-center gap-2 transition-all duration-300 cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66C2B6] focus-visible:ring-offset-2"
        aria-label="Extend the Circle of Care"
        data-testid="referral-icon"
      >
        {/* Connected Loop Icon (Circle of Care) */}
        <div className="relative">
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`transition-all duration-300 ${
              phase === 2 ? 'animate-pulse-slow' : ''
            }`}
          >
            {/* Outer circle */}
            <circle
              cx="14"
              cy="14"
              r="12"
              stroke={getIconColor()}
              strokeWidth="2"
              fill="none"
            />
            {/* Inner connected loops forming a care symbol */}
            <path
              d="M9 14C9 14 10 11 14 11C18 11 19 14 19 14C19 14 18 17 14 17C10 17 9 14 9 14Z"
              stroke={getIconColor()}
              strokeWidth="1.5"
              fill="none"
            />
            {/* Connecting hands/hearts */}
            <circle cx="8" cy="14" r="2" fill={getIconColor()} opacity="0.6" />
            <circle cx="20" cy="14" r="2" fill={getIconColor()} opacity="0.6" />
          </svg>
          
          {/* Pulsing glow effect for Phase 2 */}
          {phase === 2 && !isHovered && (
            <div 
              className="absolute inset-0 rounded-full animate-pulse-glow"
              style={{
                backgroundColor: '#66C2B6',
                filter: 'blur(8px)',
                opacity: 0.4,
                zIndex: -1
              }}
            />
          )}
        </div>
      </button>

      {/* Phase 3: Tooltip on hover - now shows at any phase */}
      {isHovered && (
        <div 
          className="absolute top-full right-0 mt-2 bg-white shadow-lg rounded-lg px-4 py-2 whitespace-nowrap z-50 animate-fade-in border border-gray-100"
          style={{ 
            backgroundColor: 'white',
            color: '#499F94'
          }}
        >
          <div className="text-sm font-medium">
            Extend the Circle of Care
          </div>
          <div className="absolute -top-2 right-4 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-white" />
        </div>
      )}
    </div>
  );
};

export default ReferralIcon;
